import { Transport } from './lib/transport.js'
import DHT from '@hyperswarm/dht-relay'
import Stream from '@hyperswarm/dht-relay/ws'
import { idbStorage, requestPersistence } from './lib/idb-storage.js'
import { adaptRelayNode } from './lib/relay-compat.js'

export const DEFAULT_RELAYS = [
  'wss://papi.olshub.xyz/relay'
]

const CONNECT_TIMEOUT = 12000

// The relay pings every fifteen seconds (@hyperswarm/dht-relay's heartbeat);
// four of those missed in a row is a link that is not there any more.
const SILENT_MAX = 60 * 1000

/**
 * Browsers cannot open UDP sockets, so they cannot speak to the DHT directly.
 * We tunnel HyperDHT over a WebSocket to a relay instead.
 *
 * The relay is a dumb pipe: every stream that passes through it is already
 * Noise-encrypted end to end, and room data is encrypted a second time with
 * the room key that the relay never sees. A hostile relay can drop traffic or
 * learn who is connecting to whom, but not read a single message.
 */
export function createTransport (opts = {}) {
  const relays = opts.relays && opts.relays.length ? opts.relays : DEFAULT_RELAYS
  const silentMax = opts.silentMax || SILENT_MAX
  let index = 0

  // Browsers evict IndexedDB under storage pressure. A messenger losing its
  // history to a cache sweep is a data-loss bug, so ask to be exempt.
  requestPersistence().catch(() => {})

  const transport = new Transport({
    ...opts,
    /*
     * Ask for fewer blocks at once than a native peer would.
     *
     * Everything a peer sends a browser is queued on the relay until the
     * browser's socket takes it, and the relay cannot slow the sender down. At
     * Hypercore's default a phone fetching a photo over mobile data asked for
     * about 3 MB per chat at a time — all of it landing in that queue faster
     * than the phone could drain it. 24 blocks in flight is still ample for
     * a fast link and keeps the queue short on a slow one.
     */
    storeOptions: { inflightRange: [8, 32] },
    createStorage: (storage, name) => storage || idbStorage(name),
    createDHT: async ({ onLost } = {}) => {
      let lastError = null
      for (let attempt = 0; attempt < relays.length; attempt++) {
        const url = relays[index % relays.length]
        index++
        let socket = null
        try {
          socket = await openSocket(url)

          /*
           * Report the socket's death upwards ourselves.
           *
           * @hyperswarm/dht-relay's node is an EventEmitter that never emits
           * 'close': when the WebSocket goes, its stream is destroyed and
           * nothing above hears about it. The transport therefore went on
           * believing it was online — no reconnect, no "reconnecting" in the
           * UI, and every message queued into a pipe with no far end. On
           * Android that happens on the ordinary path (screen off, Wi-Fi to
           * mobile data, the relay restarting), which is why the phone would
           * deliver for a while after launch and then quietly stop.
           */
          let committed = false
          const report = () => { if (committed && onLost) onLost() }
          socket.addEventListener('close', report)
          socket.addEventListener('error', report)
          let heard = Date.now()
          socket.addEventListener('message', () => { heard = Date.now() })

          // Adapted before Hyperswarm sees a single connection from it; see
          // relay-compat.js for what a relayed stream was missing.
          const dht = adaptRelayNode(new DHT(new Stream(true, socket)))
          await handshake(dht, socket)
          committed = true

          return {
            dht,
            mode: 'relay',
            relay: url,
            alive: () => socket.readyState === WebSocket.OPEN,
            fresh: () => Date.now() - heard < silentMax
          }
        } catch (err) {
          lastError = err
          // A half-open socket left behind counts against the relay's
          // per-address limit until it times out. Close it now.
          if (socket) try { socket.close() } catch {}
        }
      }
      throw new Error('Could not reach any Pine relay: ' + (lastError ? lastError.message : 'unknown'))
    }
  })

  watchForWakeups(transport)
  return transport
}

/**
 * Come back the moment the platform says something changed.
 *
 * A backgrounded tab — and every backgrounded WebView — has its timers
 * throttled to a crawl, so a reconnect scheduled before the screen went off
 * can be minutes late. These events fire on resume, and each one is a reason
 * to check the link straight away rather than on the backoff clock.
 */
function watchForWakeups (transport) {
  if (typeof window === 'undefined') return

  const wake = () => transport.wake()

  window.addEventListener('online', wake)
  window.addEventListener('pageshow', wake)
  window.addEventListener('focus', wake)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) wake()
    })
  }

  /*
   * A different kind of network — Wi-Fi to mobile data and back.
   *
   * The browser does not fire 'online' for that: it was online before and is
   * online after. But the socket to the relay belonged to the old network,
   * and it stays "open" there for minutes, carrying nothing. Only the kind of
   * network is compared — the speed estimates change all the time and are
   * no reason to drop anything. (A VPN going up or down does not change the
   * kind; the Android shell reports that itself — see NetworkWatch.kt.)
   */
  const connection = typeof navigator !== 'undefined' ? navigator.connection : null
  if (connection && typeof connection.addEventListener === 'function' && 'type' in connection) {
    let kind = connection.type
    connection.addEventListener('change', () => {
      if (connection.type === kind) return
      kind = connection.type
      if (kind !== 'none') transport.reconnect()
    })
  }
}

/**
 * Wait for the relay handshake, but not forever.
 *
 * dht.ready() resolves on the relay's handshake frame. If the socket dies in
 * between — which a captive portal and a half-open mobile connection both
 * manage — nothing rejects it, and the whole client sits in "connecting" with
 * no retry. Losing the socket is a failure of this attempt, so say so and let
 * the caller try the next relay.
 */
function handshake (dht, socket) {
  return new Promise((resolve, reject) => {
    let settled = false
    const finish = (err) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      socket.removeEventListener('close', onClose)
      if (err) reject(err)
      else resolve()
    }
    const onClose = () => finish(new Error('Relay closed during handshake'))
    const timer = setTimeout(() => finish(new Error('Relay handshake timed out')), CONNECT_TIMEOUT)

    socket.addEventListener('close', onClose)
    dht.ready().then(() => finish(null), finish)
  })
}

function openSocket (url) {
  return new Promise((resolve, reject) => {
    let settled = false
    const socket = new WebSocket(url)
    socket.binaryType = 'arraybuffer'

    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      try { socket.close() } catch {}
      reject(new Error('Relay timed out: ' + url))
    }, CONNECT_TIMEOUT)

    socket.onopen = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(socket)
    }
    socket.onerror = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      reject(new Error('Relay unreachable: ' + url))
    }
  })
}

export { Transport }
export const RUNTIME = 'browser'
